import { z } from 'zod';

/**
 * I conti dell'azienda dal bilancio, voce per voce (ADR 0132): la sezione «Conti» del fascicolo, subito dopo l'atto. Per
 * ogni esercizio le voci con codice, importo e fonte, i dati della nota integrativa con la frase da cui vengono e pochi
 * rapporti calcolati senza interpretazione. Poi ciò che non coincide tra visura, bilancio, verbali e altri documenti.
 */

/**
 * Da dove viene una voce: l'istanza XBRL depositata (tassonomia itcc-ci), il piano dei conti (bilancio di verifica conto
 * per conto), gli schemi del bilancio letti dal documento, o la nota integrativa.
 */
export const ACCOUNT_SOURCES = ['xbrl', 'piano_dei_conti', 'bilancio', 'nota_integrativa'] as const;
export type AccountSource = (typeof ACCOUNT_SOURCES)[number];

export interface AccountItemDto {
  /** Il codice come lo scrive la fonte: il conto del piano dei conti, l'elemento XBRL, la numerazione dello schema. */
  codice: string;
  descrizione: string;
  importo: number;
  /** La parte del bilancio come la scrive la fonte (es. «Stato patrimoniale · Attività», «Conto economico · Ricavi»). */
  parte: string | null;
  /** Il conto raccoglie altri conti (un mastro o un totale): non si somma con quelli. */
  aggregato: boolean;
  fonte: AccountSource;
}

/** Un conto del bilancio citato da una voce del progetto, con l'importo dell'ultimo esercizio in cui c'è (ADR 0132). */
export interface AccountRefDto {
  codice: string;
  descrizione: string;
  importo: number;
  /** La chiusura dell'esercizio, AAAA-MM-GG. */
  chiuso_il: string;
}

/** I dati della nota integrativa, in sezioni fisse. */
export const NOTE_SECTIONS = ['anagrafica', 'dipendenti', 'ricavi_per_attivita', 'partecipazioni', 'compensi', 'garanzie', 'contributi'] as const;
export type NoteSection = (typeof NOTE_SECTIONS)[number];

export interface NoteItemDto {
  campo: string;
  valore: string;
  citazione: string;
}

/** I rapporti calcolati dal server sui conti scelti dal cervello: numeri, nessun giudizio. */
export const ACCOUNT_RATIOS = ['costo_personale_per_addetto', 'ricavi_per_addetto', 'utenze_su_ricavi', 'giorni_di_incasso'] as const;
export type AccountRatio = (typeof ACCOUNT_RATIOS)[number];

export interface AccountRatioDto {
  chiave: AccountRatio;
  titolo: string;
  /** null se manca un termine (per esempio i dipendenti medi). */
  valore: number | null;
  unita: '€' | '%' | 'giorni';
  /** Come si calcola, in parole («costo del personale ÷ dipendenti medi»). */
  formula: string;
  /** I termini con i conti che li compongono e la loro somma. */
  termini: Array<{ nome: string; importo: number | null; conti: string[] }>;
  /** Perché manca, se manca. */
  nota: string | null;
  /** Lo stesso numero per le imprese del settore e della stessa classe di addetti (ISTAT), quando c'è. */
  settore?: { valore: number; fonte: string } | null;
}

export interface FinancialYearDto {
  /** La data di chiusura dell'esercizio, AAAA-MM-GG. */
  chiuso_il: string;
  /** I documenti da cui vengono le voci di questo esercizio. */
  fonti: Array<{ fonte: AccountSource; documento: string; documento_id: string }>;
  voci: AccountItemDto[];
  nota: Array<{ chiave: NoteSection; titolo: string; voci: NoteItemDto[] }>;
  rapporti: AccountRatioDto[];
  /** I conti che devono tornare (es. la somma dei conti con il totale scritto). */
  controlli: Array<{ cosa: string; ok: boolean; dettaglio: string }>;
}

/** Un dato che non coincide tra due o più documenti (ADR 0132): diventa una domanda o una voce, mai una correzione. */
export interface DocumentMismatchDto {
  id: string;
  /** Il dato, in parole (es. «Capitale sociale»). */
  tema: string;
  /** Ciò che scrive ogni documento, con la frase copiata. */
  valori: Array<{ documento: string; documento_id: string; valore: string; citazione: string }>;
  /** Il documento che vale (la visura sui dati legali), con il perché; null se non si sa. */
  prevale: string | null;
  esito: 'domanda' | 'voce';
  /** Con «domanda»: la domanda al titolare e le risposte possibili, la più probabile per prima. */
  domanda: string | null;
  opzioni: string[];
  /** Con «voce»: ciò che il progetto deve avere (es. un calendario, un allineamento), con la norma se c'è. */
  voce: string | null;
  norma: string | null;
  /** La risposta del titolare, se l'ha data: entra nelle sue decisioni. */
  risposta: string | null;
}

/** Un fatto che il consulente ricava dai conti (ADR 0132): ciò che rivelano di come lavora oggi l'azienda o cosa è cambiato. */
export interface AccountFactDto {
  testo: string;
  /** oggi: come lavora l'azienda; variazione: cosa è cambiato rispetto all'esercizio prima. */
  tipo: 'oggi' | 'variazione';
  /** Il passo della configurazione che cambia. */
  passo: string;
  /** Da dove nasce: i conti con la loro chiave e le frasi dei documenti, controllati. */
  fonti: Array<{ conto: string | null; documento: string | null; citazione: string | null }>;
}

/** Un numero dell'azienda senza bilancio (ADR 0155): fatturato, totale di bilancio, addetti, con la fonte. */
export interface AccountOnlineFactDto {
  campo: 'fatturato' | 'totale_bilancio' | 'anno_bilancio' | 'addetti';
  valore: string;
  /** visura, sito, registro (pagina pubblica con la partita IVA), online, persona… */
  fonte: string;
  /** La pagina o il documento da cui viene. */
  rif: string | null;
  citazione: string | null;
}

/** Una domanda che i conti fanno nascere: ciò che le fonti non dicono e che cambia il progetto. */
export interface AccountQuestionDto {
  id: string;
  domanda: string;
  perche: string;
  opzioni: string[];
  passo: string;
  risposta: string | null;
}

export interface AccountsDto {
  /** assente: nessun bilancio; in_lettura: il consulente lo sta leggendo; pronto. */
  stato: 'assente' | 'in_lettura' | 'pronto';
  esercizi: FinancialYearDto[];
  /**
   * Cosa chiedere al titolare. Dall'ADR 0155 il bilancio non si chiede: si scarica tramite API o, se non si può, i numeri
   * vengono dalle pagine online con la fonte; resta null.
   */
  da_caricare: string | null;
  /** Da dove vengono i numeri (ADR 0155): il bilancio scaricato tramite API, o perché non c'è e cosa si usa al suo posto. */
  origine?: string | null;
  /** Senza bilancio (ADR 0155): i numeri dell'azienda trovati online o in visura, ognuno con la sua fonte. */
  online?: AccountOnlineFactDto[];
  controllo: { stato: 'assente' | 'in_corso' | 'pronto' | 'errore'; incoerenze: DocumentMismatchDto[] };
  /** La lettura del consulente: cosa dicono i conti, cosa è cambiato, le domande e cosa manca per dire di più. */
  lettura?: { fatti: AccountFactDto[]; domande: AccountQuestionDto[]; mancano: string[] };
  /** I bilanci che non si sono potuti leggere, con il perché. */
  non_letti?: Array<{ documento: string; errore: string }>;
}

/** La risposta del titolare a una domanda del controllo tra documenti (ADR 0132). */
export const AccountsAnswerSchema = z.object({ id: z.string().trim().min(1).max(120), risposta: z.string().trim().min(1).max(500) }).strict();
export type AccountsAnswerInput = z.infer<typeof AccountsAnswerSchema>;
